import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NgStyle } from '@angular/common';
import {
    RenderedSignatureField,
    SIGNATURE_ROLE_COLORS_LIGHT,
    SIGNATURE_ROLE_COLORS_DARK,
    getSignatureRoleDisplayName
} from '@trade-platform/ui-shared';

const TYPE_ICONS: Record<string, string> = {
    Signature: 'fa-regular fa-pen-fancy',
    DateSigned: 'fa-regular fa-calendar',
    Text: 'fa-regular fa-font'
};

const TYPE_SIZES: Record<string, { width: number; height: number }> = {
    Signature: { width: 150, height: 32 },
    DateSigned: { width: 100, height: 20 },
    Text: { width: 120, height: 20 }
};

@Component({
    selector: 'aix-signature-overlay',
    standalone: true,
    imports: [NgStyle],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @for (field of pageFields(); track field.id) {
        <div
            class="sig-overlay-field"
            data-testing="signature-overlay-field"
            [ngStyle]="getFieldStyle(field)"
        >
            <i [class]="getTypeIcon(field.type)"></i>
            <span class="sig-overlay-field__role">{{ getRoleDisplayName(field) }}</span>
        </div>
        }
    `,
    styles: [
        `
            :host {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
                z-index: 1;
            }

            .sig-overlay-field {
                position: absolute;
                display: flex;
                align-items: center;
                gap: 4px;
                padding: 2px 6px;
                border-radius: 3px;
                font-size: 10px;
                font-family: sans-serif;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                box-sizing: border-box;
                pointer-events: auto;

                &__role {
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }
        `
    ]
})
export class AixSignatureOverlayComponent {
    signatureFields = input<RenderedSignatureField[]>([]);
    pageNumber = input<number>(0);
    scaleRatio = input<number>(1);
    hiddenRoles = input<Set<string>>(new Set());
    wetSignRoles = input<Set<string>>(new Set());
    /** groupKey -> display label, computed by the parent so overlay labels match the sidebar. */
    roleLabels = input<Map<string, string>>(new Map());

    private roleColorMap = computed(() => {
        const map = new Map<string, number>();
        const fields = this.signatureFields();
        let idx = 0;
        for (const f of fields) {
            const key = f.groupKey;
            if (!map.has(key)) {
                map.set(key, idx % SIGNATURE_ROLE_COLORS_DARK.length);
                idx++;
            }
        }
        return map;
    });

    pageFields = computed(() => {
        const page = this.pageNumber();
        const hidden = this.hiddenRoles();
        return this.signatureFields().filter(
            f => f.pageNumber === page + 1 && !hidden.has(f.groupKey)
        );
    });

    getFieldStyle(field: RenderedSignatureField): Record<string, string> {
        const scale = this.scaleRatio();
        const key = field.groupKey;
        const colorIdx = this.roleColorMap().get(key) ?? 0;
        const size = TYPE_SIZES[field.type] || TYPE_SIZES['Text'];
        const isWetSign = this.wetSignRoles().has(key);

        const baseStyle: Record<string, string> = {
            left: `${field.xPosition * scale}px`,
            top: `${(field.yPosition - size.height / 2) * scale}px`,
            width: `${size.width * scale}px`,
            height: `${size.height * scale}px`,
            border: `1.5px solid ${SIGNATURE_ROLE_COLORS_DARK[colorIdx]}`,
            fontSize: `${Math.max(8, 10 * scale)}px`
        };

        if (isWetSign) {
            const stripeColor = SIGNATURE_ROLE_COLORS_LIGHT[colorIdx];
            baseStyle[
                'background'
            ] = `repeating-linear-gradient(45deg, ${stripeColor}, ${stripeColor} 4px, transparent 4px, transparent 8px)`;
        } else {
            baseStyle['backgroundColor'] = SIGNATURE_ROLE_COLORS_LIGHT[colorIdx];
        }

        return baseStyle;
    }

    getRoleDisplayName(field: RenderedSignatureField): string {
        return (
            this.roleLabels().get(field.groupKey) ??
            getSignatureRoleDisplayName(field.roleName || field.role)
        );
    }

    getTypeIcon(type: string): string {
        return TYPE_ICONS[type] || TYPE_ICONS['Text'];
    }
}
